import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome, ${data.name}`);
      navigate(data.role === "admin" ? "/admin" : "/partner", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex flex-col">
      <Seo title="Sign in" description="Sign in to your CareHomeConnect admin or partner account." path="/login" noindex />
      <div className="p-6 md:p-10">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="login-brand">
          <div className="w-9 h-9 rounded-full bg-[#2C4C3B] flex items-center justify-center">
            <Heart className="w-5 h-5 text-[#F7F5F0]" fill="currentColor" />
          </div>
          <span className="font-display text-xl font-semibold text-[#2C4C3B]">
            CareHome<span className="text-[#9CAF88]">Connect</span>
          </span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] tracking-tight">
            Sign in
          </h1>
          <p className="mt-2 text-[#5C6B62]">Admin & partner dashboard access.</p>

          <form onSubmit={submit} className="card-soft p-6 md:p-8 mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#2C4C3B] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#2C4C3B] font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 font-medium"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-xl bg-[#9CAF88]/10 text-xs text-[#5C6B62]">
            <div className="font-medium text-[#2C4C3B] mb-1">Demo credentials</div>
            <div>Admin: admin@carehomeconnect.com / Admin@123</div>
            <div>Partner: partner@carehomeconnect.com / Partner@123</div>
          </div>
        </div>
      </div>
    </div>
  );
}
